import { useState, useEffect } from 'react';

const DAYS_TILL_EXPIRY = 30;

/**
 * Sets a cookie with a specified name, value, and expiration in days.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to store in the cookie.
 * @param {number} days - Number of days until the cookie expires.
 */
const setCookie = (name: string, value: string) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + DAYS_TILL_EXPIRY);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/`;
};

/**
 * Retrieves a cookie value by its name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | null} - The cookie value or null if not found.
 */
const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split('; ').reduce((acc, cookie) => {
    const [key, val] = cookie.split('=');
    acc[key] = val;
    return acc;
  }, {} as Record<string, string>);

  return cookies[name] || null;
};

/**
 * Custom React hook to manage a cookie statefully.
 *
 * @param {string} key - The name of the cookie to manage.
 * @returns {[string | null, React.Dispatch<React.SetStateAction<string | null>>]} - The cookie value and a setter function.
 */
export const useCookie = (key: string) => {
  const [cookie, setCookieState] = useState<string | null>(() => getCookie(key));

  useEffect(() => {
    setCookie(key, cookie || '');
  }, [cookie, key]);

  return [cookie, setCookieState] as const;
};
